/**
 * Provisioning locale del token (Raspberry kiosk): un piccolo server su 127.0.0.1 tiene una copia del token su una
 * partizione persistente, così se il profilo Chromium si perde il player non richiede un nuovo abbinamento.
 *   GET    -> 200 {"device_token": "..."} | 404
 *   PUT    {"device_token": "..."} -> 204
 *   DELETE -> 204
 * Tutto best effort: nessuna funzione lancia verso il chiamante.
 */

export type FetchFn = typeof fetch;

export const PROVISION_TIMEOUT_MS = 5_000;
const MAX_TOKEN_LEN = 512;

async function call(url: string, init: RequestInit, fetchFn: FetchFn): Promise<Response | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), PROVISION_TIMEOUT_MS);
  try {
    return await fetchFn(url, { ...init, signal: ctrl.signal });
  } catch {
    return null; // rete, abort, URL non valido
  } finally {
    clearTimeout(timer);
  }
}

/** Token salvato nel server locale, o null (assente, non valido, errore). */
export async function loadProvisionedToken(url: string, fetchFn: FetchFn = globalThis.fetch.bind(globalThis)): Promise<string | null> {
  const res = await call(url, { method: 'GET', cache: 'no-store' }, fetchFn);
  if (!res || res.status !== 200) return null;
  try {
    const body = (await res.json()) as { device_token?: unknown } | null;
    const t = body?.device_token;
    return typeof t === 'string' && t.length > 0 && t.length <= MAX_TOKEN_LEN ? t : null;
  } catch {
    return null;
  }
}

export async function saveProvisionedToken(url: string, token: string, fetchFn: FetchFn = globalThis.fetch.bind(globalThis)): Promise<boolean> {
  const res = await call(url, {
    method: 'PUT',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_token: token }),
  }, fetchFn);
  return !!res && res.ok;
}

export async function clearProvisionedToken(url: string, fetchFn: FetchFn = globalThis.fetch.bind(globalThis)): Promise<boolean> {
  const res = await call(url, { method: 'DELETE', cache: 'no-store' }, fetchFn);
  return !!res && res.ok;
}
